import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { Button } from '@/components/ui/button';
import { BellRing, CheckCircle2, X, AlarmClockOff } from 'lucide-react';
import { resolveStepTimes, parseRouteStartTime, parseHHMM } from '@/lib/protocolSchedule';
import { resolveAutoDay } from '@/lib/autoDay';
import { shouldReFireWake, escalationProfile, nextSnoozeFireMs } from '@/lib/myDayEscalation';

// While the app is open, arms in-app alarms for today's upcoming 'alarm'
// protocol steps (the sub's ACTIVE opt-ins only) and "My Day — automatic"
// steps (protocol_day_settings enabled + travel known + route start
// published). At fire time: full-screen overlay + looping WebAudio beeps +
// vibration. The push notification for the same step comes from the
// dispatcher independently — this is the "app is open right now" experience,
// so a driver mid-breakfast can't miss it.
//
// Refire guards: a step already marked done today never alarms; a dismissed
// alarm is remembered in localStorage per step+date (auto-day steps use their
// step_key as the id). If WebAudio is blocked (no prior user gesture), the
// overlay + vibration still happen.

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dismissKey = (stepId, date) => `protocol_alarm_dismissed:${stepId}:${date}`;
const wasDismissed = (stepId, date) => {
  try { return window.localStorage.getItem(dismissKey(stepId, date)) === '1'; } catch (_e) { return false; }
};
const rememberDismissed = (stepId, date) => {
  try { window.localStorage.setItem(dismissKey(stepId, date), '1'); } catch (_e) { /* private mode */ }
};

const fmtTime = (date) =>
  date ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

const ProtocolAlarmManager = () => {
  const { profile } = useSubcontractorProfile();
  const [activeAlarm, setActiveAlarm] = useState(null); // { step, fireAt, protocolId }
  const [escalationLabel, setEscalationLabel] = useState('');
  const timeoutsRef = useRef([]);
  const audioRef = useRef(null);     // { ctx, osc, gain, toneInterval }
  const vibrateRef = useRef(null);   // interval id
  const activeAlarmRef = useRef(null);
  const firedAtRef = useRef(0);       // when the current alarm last (re-)fired
  const refireCountRef = useRef(0);   // how many times it has escalated
  const escalationTimerRef = useRef(null);
  const snoozeRef = useRef(null);     // { alarm, fireMs, timer } — survives re-arms

  const clearTimeouts = () => {
    for (const t of timeoutsRef.current) clearTimeout(t);
    timeoutsRef.current = [];
  };

  const stopNoise = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try { clearInterval(a.toneInterval); a.osc.stop(); a.ctx.close(); } catch (_e) { /* already closed */ }
      audioRef.current = null;
    }
    if (vibrateRef.current) {
      clearInterval(vibrateRef.current);
      vibrateRef.current = null;
      try { navigator.vibrate && navigator.vibrate(0); } catch (_e) { /* unsupported */ }
    }
  }, []);

  const startNoise = useCallback((profile = { gain: 0.12, beepMs: 350 }) => {
    stopNoise(); // ramping up: replace any running tone with the louder profile
    const vol = profile.gain ?? 0.12;
    const beepMs = profile.beepMs ?? 350;
    // Alternating 880/660Hz beeps — no audio asset needed. If the context is
    // blocked (no prior user gesture), fail silently: overlay + vibrate remain.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = 880;
        gain.gain.value = vol;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        let high = true;
        let beepOn = true;
        const toneInterval = setInterval(() => {
          // Beep pattern: tone, silence, alternating pitch — cadence tightens as
          // the alarm escalates.
          beepOn = !beepOn;
          gain.gain.value = beepOn ? vol : 0;
          if (beepOn) {
            high = !high;
            osc.frequency.value = high ? 880 : 660;
          }
        }, beepMs);
        audioRef.current = { ctx, osc, gain, toneInterval };
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      }
    } catch (_e) { /* WebAudio unavailable — overlay + vibration still fire */ }

    try {
      if (navigator.vibrate) {
        navigator.vibrate([400, 150, 400, 150, 600]);
        vibrateRef.current = setInterval(() => navigator.vibrate([400, 150, 400, 150, 600]), 2200);
      }
    } catch (_e) { /* unsupported */ }
  }, [stopNoise]);

  // Escalating wake: while a wake alarm sits unattended, re-fire louder every few
  // minutes (up to a cap), then stop nagging. Uses the pure decision helpers so
  // the behaviour is unit-tested. Only the wake step escalates.
  const isWake = (alarm) => alarm?.autoKey === 'auto:wake';
  const clearEscalation = () => {
    if (escalationTimerRef.current) { clearInterval(escalationTimerRef.current); escalationTimerRef.current = null; }
  };
  const startEscalation = useCallback((alarm) => {
    clearEscalation();
    if (!isWake(alarm)) return;
    escalationTimerRef.current = setInterval(() => {
      const a = activeAlarmRef.current;
      if (!a || !isWake(a)) { clearEscalation(); return; }
      const decide = shouldReFireWake({
        nowMs: Date.now(), lastFiredMs: firedAtRef.current,
        status: 'notified', dismissed: false, refireCount: refireCountRef.current,
      });
      if (!decide) return;
      refireCountRef.current += 1;
      firedAtRef.current = Date.now();
      const prof = escalationProfile(refireCountRef.current);
      setEscalationLabel(prof.label);
      startNoise(prof);
      try { navigator.vibrate && navigator.vibrate([500, 120, 500, 120, 800]); } catch (_e) { /* unsupported */ }
    }, 30 * 1000);
  }, [startNoise]);

  const fireAlarm = useCallback((alarm) => {
    const date = todayStr();
    if (wasDismissed(alarm.step.id, date)) return;
    if (activeAlarmRef.current) return; // one at a time; the next re-arm catches missed ones
    activeAlarmRef.current = alarm;
    firedAtRef.current = Date.now();
    refireCountRef.current = 0;
    setEscalationLabel('');
    setActiveAlarm(alarm);
    startNoise(escalationProfile(0));
    startEscalation(alarm);
  }, [startNoise, startEscalation]);

  const armAlarms = useCallback(async () => {
    clearTimeouts();
    // A pending snooze must survive every re-arm. Timers die while a mobile
    // tab is backgrounded, so reconcile against wall-clock: overdue → ring
    // now; still pending → reschedule on a fresh timer.
    if (snoozeRef.current) {
      const { alarm, fireMs, timer } = snoozeRef.current;
      clearTimeout(timer);
      if (fireMs <= Date.now()) {
        snoozeRef.current = null;
        fireAlarm(alarm);
      } else {
        snoozeRef.current = {
          alarm,
          fireMs,
          timer: setTimeout(() => { snoozeRef.current = null; fireAlarm(alarm); }, fireMs - Date.now()),
        };
      }
    }
    if (!profile?.id || profile.role !== 'subcontractor') return;
    const date = todayStr();

    try {
      const [{ data: asgRows }, { data: allocRows }, { data: daySettings }] = await Promise.all([
        supabase
          .from('protocol_assignments')
          .select('protocol_id, protocols!inner(id, name, active, run_days, protocol_steps(*))')
          .eq('subcontractor_id', profile.id)
          .eq('active', true)
          .eq('protocols.active', true),
        supabase.from('crew_allocations').select('crew_id').eq('subcontractor_id', profile.id).eq('date', date).limit(1),
        supabase
          .from('protocol_day_settings')
          .select('auto_enabled, travel_minutes, prep_minutes, travel_source')
          .eq('subcontractor_id', profile.id)
          .maybeSingle(),
      ]);

      const crewId = allocRows?.[0]?.crew_id || null;
      let routeStartHHMM = null;
      if (crewId) {
        const { data: routes } = await supabase
          .from('dropbox_route_files')
          .select('extracted_text')
          .eq('crew_id', crewId)
          .eq('route_date', date);
        const starts = (routes || []).map((r) => parseRouteStartTime(r.extracted_text)).filter(Boolean).sort();
        routeStartHHMM = starts[0] || null;
      }

      // Today's events: steps already done never alarm again (auto-day steps
      // key by step_key; protocol steps by step_id).
      const { data: eventRows } = await supabase
        .from('protocol_step_events')
        .select('step_id, step_key, status')
        .eq('subcontractor_id', profile.id)
        .eq('date', date);
      const doneRows = (eventRows || []).filter((e) => e.status === 'done');
      const doneSteps = new Set(doneRows.map((e) => e.step_id).filter(Boolean));
      const doneAutoKeys = new Set(doneRows.map((e) => e.step_key).filter(Boolean));

      const now = Date.now();
      for (const asg of asgRows || []) {
        const protocol = asg.protocols;
        if (!protocol) continue;
        if (protocol.run_days !== 'every_day' && !crewId) continue; // not a run day

        const resolved = resolveStepTimes(protocol.protocol_steps || [], { routeStartHHMM, date });
        for (const r of resolved) {
          if (r.step.alert_level !== 'alarm' || !r.fireAt) continue;
          if (doneSteps.has(r.step.id) || wasDismissed(r.step.id, date)) continue;
          const delay = r.fireAt.getTime() - now;
          if (delay <= 0 || delay > 24 * 60 * 60 * 1000) continue; // only today's upcoming
          const alarm = { step: r.step, fireAt: r.fireAt, protocolId: protocol.id, protocolName: protocol.name };
          timeoutsRef.current.push(setTimeout(() => fireAlarm(alarm), delay));
        }
      }

      // "My Day — automatic": alarm-level auto steps, same overlay/tone.
      // Their step_key doubles as the id for the dismissal guard.
      if (routeStartHHMM) {
        const plan = resolveAutoDay(daySettings, parseHHMM(routeStartHHMM));
        for (const s of plan) {
          if (s.level !== 'alarm') continue;
          if (doneAutoKeys.has(s.key) || wasDismissed(s.key, date)) continue;
          const [y, mo, d] = date.split('-').map(Number);
          const fireAt = new Date(y, mo - 1, d, Math.floor(s.minutes / 60), s.minutes % 60, 0, 0);
          const delay = fireAt.getTime() - now;
          if (delay <= 0 || delay > 24 * 60 * 60 * 1000) continue; // only today's upcoming
          const alarm = {
            step: { id: s.key, title: s.title, guidance: s.guidance, alert_level: 'alarm' },
            fireAt,
            protocolName: 'My Day',
            autoKey: s.key,
          };
          timeoutsRef.current.push(setTimeout(() => fireAlarm(alarm), delay));
        }
      }
    } catch (e) {
      console.error('ProtocolAlarmManager: arm failed', e);
    }
  }, [profile?.id, profile?.role, fireAlarm]);

  useEffect(() => {
    armAlarms();
    // Timers drift/die while a mobile tab is backgrounded — re-arm on return.
    const onVisible = () => { if (document.visibilityState === 'visible') armAlarms(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeouts();
      if (snoozeRef.current?.timer) { clearTimeout(snoozeRef.current.timer); snoozeRef.current = null; }
      clearEscalation();
      stopNoise();
    };
  }, [armAlarms, stopNoise]);

  const closeAlarm = useCallback((alarm) => {
    clearEscalation();
    stopNoise();
    rememberDismissed(alarm.step.id, todayStr());
    activeAlarmRef.current = null;
    setActiveAlarm(null);
    setEscalationLabel('');
    armAlarms(); // pick up the next one
  }, [stopNoise, armAlarms]);

  // Snooze RE-ARMS a fresh timer (not a one-shot) and, crucially, does NOT
  // record a dismissal — the wake will ring again after the snooze window.
  // The snooze lives in its own ref, NOT timeoutsRef: re-arms wipe timeoutsRef
  // and skip past-due steps, which used to silently swallow a snooze the
  // moment the phone was backgrounded.
  const snoozeAlarm = useCallback((alarm) => {
    clearEscalation();
    stopNoise();
    const fireMs = nextSnoozeFireMs(Date.now(), 9);
    activeAlarmRef.current = null;
    setActiveAlarm(null);
    setEscalationLabel('');
    if (fireMs) {
      if (snoozeRef.current?.timer) clearTimeout(snoozeRef.current.timer);
      snoozeRef.current = {
        alarm,
        fireMs,
        timer: setTimeout(() => { snoozeRef.current = null; fireAlarm(alarm); }, Math.max(0, fireMs - Date.now())),
      };
    }
  }, [stopNoise, fireAlarm]);

  const handleDone = async (alarm) => {
    try {
      if (alarm.autoKey) {
        // Synthetic auto-day step: keyed by (sub, date, step_key), step_id
        // null. 23505 = the dispatcher already inserted 'notified' — flip it.
        const patch = { status: 'done', completed_at: new Date().toISOString() };
        const { error } = await supabase
          .from('protocol_step_events')
          .insert({ subcontractor_id: profile.id, step_id: null, step_key: alarm.autoKey, date: todayStr(), ...patch });
        if (error?.code === '23505') {
          await supabase
            .from('protocol_step_events')
            .update(patch)
            .eq('subcontractor_id', profile.id)
            .eq('date', todayStr())
            .eq('step_key', alarm.autoKey);
        }
      } else {
        await supabase
          .from('protocol_step_events')
          .upsert({
            subcontractor_id: profile.id,
            protocol_id: alarm.protocolId,
            step_id: alarm.step.id,
            date: todayStr(),
            status: 'done',
            completed_at: new Date().toISOString(),
          }, { onConflict: 'step_id,subcontractor_id,date' });
      }
    } catch (e) {
      console.error('ProtocolAlarmManager: mark done failed', e);
    }
    closeAlarm(alarm);
  };

  if (!activeAlarm) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-950/95 p-6 text-center">
      <div className="mb-6 grid h-24 w-24 place-items-center rounded-full bg-rose-500/15">
        <BellRing className="h-12 w-12 animate-pulse text-rose-400" />
      </div>
      <p className="text-xs font-black uppercase tracking-widest text-rose-400">
        {activeAlarm.protocolName || 'Protocol'} · {fmtTime(activeAlarm.fireAt)}
      </p>
      <h1 className="mt-2 max-w-md text-3xl font-black text-white">{activeAlarm.step.title}</h1>
      {escalationLabel && (
        <p className="mt-2 text-sm font-bold text-rose-300">{escalationLabel}</p>
      )}
      {activeAlarm.step.guidance && (
        <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300 whitespace-pre-wrap">
          {activeAlarm.step.guidance}
        </p>
      )}
      <div className="mt-8 flex w-full max-w-xs flex-col gap-3">
        <Button
          onClick={() => handleDone(activeAlarm)}
          className="h-12 w-full bg-emerald-600 text-base font-bold text-white hover:bg-emerald-700"
        >
          <CheckCircle2 className="mr-2 h-5 w-5" /> Done
        </Button>
        {activeAlarm.autoKey === 'auto:wake' && (
          <Button
            onClick={() => snoozeAlarm(activeAlarm)}
            variant="outline"
            className="h-12 w-full border-amber-600/60 bg-transparent text-base font-semibold text-amber-300 hover:bg-amber-950/40 hover:text-amber-200"
          >
            <AlarmClockOff className="mr-2 h-5 w-5" /> Snooze 9 min
          </Button>
        )}
        <Button
          onClick={() => closeAlarm(activeAlarm)}
          variant="outline"
          className="h-12 w-full border-slate-700 bg-transparent text-base font-semibold text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <X className="mr-2 h-5 w-5" /> Dismiss
        </Button>
      </div>
    </div>
  );
};

export default ProtocolAlarmManager;
